import { useMemo, useCallback } from 'react'
import * as THREE from 'three'
import { map, mapValues, reduce, isNumber, filter } from 'lodash'
import { scaleQuantile, scaleQuantize } from 'd3-scale'

import { useQueryParams, useChangeHistory } from 'flow/queryparams'

import conceptToSubject from 'data/output/concept-to-subject-dict.json'
import conceptToGrade from 'data/output/concept-to-grade-dict.json'
import colorsIndicators from 'data/output/colors-indicators.json'
import subjectToConcepts from 'data/output/subject-to-concepts-dict'
import gradeToConcepts from 'data/output/grade-to-concepts-dict'

import { useHighlightedLegendItem } from 'flow/interactions/accessors'

import useUserData from 'flow/userData/useUserData'
import useSelectedNodesIds from 'flow/graph/useSelectedNodes'

import getNature from 'common/utils/get-nature'
import FormatNumber from 'common/utils/number'

const scaleSteps = [0, 1, 2, 3, 4, 5]

const quantileScale = scaleQuantile().range(scaleSteps)
const quantizeScale = scaleQuantize().range(scaleSteps)

const colors = [
  {
    value: 'colors_subjects',
    label: 'Subjects',
    group: 'Learning Map'
  },
  {
    value: 'colors_grades',
    label: 'Grades',
    group: 'Learning Map'
  },
  ...colorsIndicators.sort((a, b) => {
    return a.label.localeCompare(b.label, undefined, {
      numeric: true,
      sensitivity: 'base'
    })
  })
]

export function useColorSetting () {
  const changeHistory = useChangeHistory()
  const { color } = useQueryParams()

  const selectedColor = useMemo(() => {
    return (color && colors.find((d) => color === d.value)) || colors[0]
  }, [color])

  const setColor = useCallback((value) => {
    changeHistory({
      color: value
    })
  }, [changeHistory])

  return [
    selectedColor,
    colors,
    setColor
  ]
}

const colorToArray = (color) => {
  const c = new THREE.Color(color)
  return [c.r, c.g, c.b]
}

const subjects = {
  Mathematics: {
    label: 'maths',
    color: '#46A6FF',
    id: 'Mathematics'
  },
  'Environmental Studies': {
    color: '#46FFA6',
    label: 'Env. stud.',
    id: 'Environmental Studies'
  },
  Physics: {
    color: '#FF4646',
    label: 'Phys.',
    id: 'Physics'
  },
  Science: {
    color: '#FFED46',
    label: 'sci.',
    id: 'Science'
  },
  Biology: {
    color: '#8C46FF',
    label: 'bio.',
    id: 'Biology'
  },
  Chemistry: {
    color: '#FF46CB',
    label: 'chem.',
    id: 'Chemistry'
  }
}

const subjectsWebGlColors = {
  Mathematics: colorToArray(subjects.Mathematics.color),
  'Environmental Studies': colorToArray(subjects['Environmental Studies'].color),
  Physics: colorToArray(subjects.Physics.color),
  Science: colorToArray(subjects.Science.color),
  Biology: colorToArray(subjects.Biology.color),
  Chemistry: colorToArray(subjects.Chemistry.color)
}

const grades = {
  'Grade 1': {
    label: 'Gr.1',
    color: '#FF4646',
    id: 'Grade 1'
  },
  'Grade 2': {
    label: '2',
    color: '#46D3FF',
    id: 'Grade 2'
  },
  'Grade 3': {
    label: '3',
    color: '#FF9F46',
    id: 'Grade 3'
  },
  'Grade 4': {
    label: '4',
    color: '#46A6FF',
    id: 'Grade 4'
  },
  'Grade 5': {
    label: '5',
    color: '#FFED46',
    id: 'Grade 5'
  },
  'Grade 6': {
    label: '6',
    color: '#5546FF',
    id: 'Grade 6'
  },
  'Grade 7': {
    label: '7',
    color: '#CFFF46',
    id: 'Grade 7'
  },
  'Grade 8': {
    label: '8',
    color: '#8C46FF',
    id: 'Grade 8'
  },
  'Grade 9': {
    label: '9',
    color: '#6BFF46',
    id: 'Grade 9'
  },
  'Grade 10': {
    label: '10',
    color: '#AE46FF',
    id: 'Grade 10'
  },
  'Grade 11': {
    label: '11',
    color: '#46FFA6',
    id: 'Grade 11'
  },
  'Grade 12': {
    label: '12',
    color: '#FF46CB',
    id: 'Grade 12'
  }
}

const gradesWebGlColors = {
  'Grade 1': colorToArray(grades['Grade 1'].color),
  'Grade 2': colorToArray(grades['Grade 2'].color),
  'Grade 3': colorToArray(grades['Grade 3'].color),
  'Grade 4': colorToArray(grades['Grade 4'].color),
  'Grade 5': colorToArray(grades['Grade 5'].color),
  'Grade 6': colorToArray(grades['Grade 6'].color),
  'Grade 7': colorToArray(grades['Grade 7'].color),
  'Grade 8': colorToArray(grades['Grade 8'].color),
  'Grade 9': colorToArray(grades['Grade 9'].color),
  'Grade 10': colorToArray(grades['Grade 10'].color),
  'Grade 11': colorToArray(grades['Grade 11'].color),
  'Grade 12': colorToArray(grades['Grade 12'].color)
}

const colorsClasses = {
  0: '#FF4646',
  1: '#FF9F46',
  2: '#FFED46',
  3: '#CFFF46',
  4: '#6BFF46',
  5: '#09E105'
}

const colorsClassesWebGl = [
  colorToArray(colorsClasses[0]),
  colorToArray(colorsClasses[1]),
  colorToArray(colorsClasses[2]),
  colorToArray(colorsClasses[3]),
  colorToArray(colorsClasses[4]),
  colorToArray(colorsClasses[5])
]

const getValues = (userData, value) => {
  if (!userData) return []
  return map(filter(userData, (d, key) => getNature(key) === 'concept'), (item) => item[value])
}

const quantileScales = [
  'average_attempts_per_question',
  'n_unique_users'
]

export const useConceptColorsById = () => {
  const [selectedColor] = useColorSetting()
  const [userData] = useUserData()
  const selectedNodes = useSelectedNodesIds()

  const colorDict = useMemo(() => {
    if (selectedColor.value === 'colors_grades') {
      return mapValues(conceptToGrade, (value) => {
        return gradesWebGlColors[value]
      })
    } else if (selectedColor.value === 'colors_subjects') {
      return mapValues(conceptToSubject, (value) => {
        return subjectsWebGlColors[value]
      })
    } else {
      const isquantile = quantileScales.includes(selectedColor.value)
      const scale = isquantile ? quantileScale : quantizeScale
      scale.domain(
        isquantile
          ? getValues(userData, selectedColor.value)
          : selectedColor.bounds
      )

      const res = reduce(selectedNodes, (sum, value) => {
        const dataExist = userData && userData[value] && isNumber(userData[value][selectedColor.value])
        const color = dataExist
          ? colorsClassesWebGl[scale(userData[value][selectedColor.value])]
          : colorToArray('#564e7c')

        sum[value] = color
        return sum
      }, {})

      return res
    }
  }, [selectedNodes, selectedColor, userData])

  return colorDict
}

const getColorScaleLabel = (item, isMin) => {
  const value = item.bounds[isMin ? 0 : 1]
  const key = value >= 2 ? 'label_sufix_plur' : 'label_sufix_sing'
  const suffix = isMin || (!isMin && !item.noMaxUnit) ? item[key] : ''
  return `${FormatNumber(value)}${suffix}`
}

export const useColorScale = () => {
  const [selectedColor] = useColorSetting()
  const [, setHighlightedLegendItem] = useHighlightedLegendItem()

  const onMouseEnter = useCallback((d) => {
    if (selectedColor.value === 'colors_grades') {
      setHighlightedLegendItem({
        id: d.id,
        conceptsIds: gradeToConcepts[d.id]
      })
    } else if (selectedColor.value === 'colors_subjects') {
      setHighlightedLegendItem({
        id: d.id,
        conceptsIds: subjectToConcepts[d.id]
      })
    }
  }, [selectedColor.value, setHighlightedLegendItem])

  const onMouseLeave = useCallback((d) => {
    setHighlightedLegendItem({
      id: null,
      conceptsIds: null
    })
  }, [setHighlightedLegendItem])

  const colorScale = useMemo(() => {
    if (selectedColor.value === 'colors_grades') {
      return map(grades, (d) => d)
    } else if (selectedColor.value === 'colors_subjects') {
      return map(subjects, (d) => d)
    } else {
      return scaleSteps.map((d) => {
        let label = null
        let isAlignRight = false

        if (d === 0) {
          label = getColorScaleLabel(selectedColor, true)
        } else if (d + 1 === scaleSteps.length) {
          label = getColorScaleLabel(selectedColor, false)
          isAlignRight = true
        }

        return {
          id: d,
          label,
          color: colorsClasses[d],
          isAlignRight
        }
      })
    }
  }, [selectedColor])

  return [colorScale, onMouseEnter, onMouseLeave]
}
